import { useEffect, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import spinnerTitle from '../others/spinnerTitle';

export interface FormSelectOption {
    value: string | number;
    label: string;
}

type FormSelectValue = string | number | null;

interface FormSelectProps {
    title?: string;
    setShowForm?: (show: boolean) => void;
    callBack: (value: FormSelectValue) => void | Promise<void>;
    defaultValue?: string | number | null;
    options: FormSelectOption[];
    emptyOptionLabel?: string;
    compact?: boolean;
}

const toSelectValue = (value: string | number | null | undefined) =>
    value === null || value === undefined ? '' : String(value);

const FormSelect = ({
    title,
    setShowForm,
    callBack,
    defaultValue = '',
    options,
    emptyOptionLabel,
    compact = false,
}: FormSelectProps) => {
    const { t } = useTranslation();
    const selectRef = useRef<HTMLSelectElement | null>(null);
    const [selectedValue, setSelectedValue] = useState(toSelectValue(defaultValue));
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        setSelectedValue(toSelectValue(defaultValue));
    }, [defaultValue]);

    useEffect(() => {
        selectRef.current?.focus();
    }, []);

    const handleCancel = () => {
        setShowForm?.(false);
    };

    const handleSave = async () => {
        setIsSubmitting(true);
        try {
            await callBack(selectedValue === '' ? null : selectedValue);
            setShowForm?.(false);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={compact ? '' : 'mt-3'}>
            <Form.Group className={compact ? 'mb-2' : 'mb-3'}>
                {title ? <label className="form-label">{title}</label> : null}
                <Form.Select
                    ref={selectRef}
                    size={compact ? 'sm' : undefined}
                    value={selectedValue}
                    disabled={isSubmitting}
                    onChange={(e) => setSelectedValue(e.target.value)}
                >
                    {emptyOptionLabel !== undefined ? <option value="">{emptyOptionLabel}</option> : null}
                    {options.map((option) => (
                        <option key={String(option.value)} value={String(option.value)}>
                            {option.label}
                        </option>
                    ))}
                </Form.Select>
            </Form.Group>
            <div className="text-end">
                <Button
                    type="button"
                    style={{height:24}}
                    onClick={handleCancel}
                    variant="secondary"
                    size="sm"
                    className="mb-1 min70px"
                    disabled={isSubmitting}
                >
                    {t('buttons.cancel')}
                </Button>
                <Button
                    type="button"
                    style={{height:24}}
                    onClick={handleSave}
                    variant="success"
                    size="sm"
                    className="mb-1 ms-2 min70px"
                    disabled={isSubmitting}
                >
                    {spinnerTitle(t('buttons.save'), isSubmitting, t('buttons.saving'))}
                </Button>
            </div>
        </div>
    );
};

export default FormSelect;
